import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor, _AbTabControl;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { renderContentWithIdentityForClassComponent } from "abreact/core/pagestate/PageStateApi";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { Orientation } from "abreact/incubator/ui/SharedTypes";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { AbButton } from "abreact/misc/AbButton";
import { AbItem } from "abreact/misc/AbItem";
import { AbPseudoButton } from "abreact/misc/AbPseudoButton";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import * as AbHooks from "abreact/utils/AbHooks";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
const LOGGER = LogUtils.getLogger(__filename);
export let AbTabControlTheme = /*#__PURE__*/function (AbTabControlTheme) {
  AbTabControlTheme["PRIMARY"] = "ab-tab-control-primary-theme";
  AbTabControlTheme["SECONDARY"] = "ab-tab-control-secondary-theme";
  return AbTabControlTheme;
}({});

/**
 * Props for the AbTabControl
 */

const locationsEqual = (l1, l2) => {
  return l1.lr === l2.lr && l1.index === l2.index;
};

/**
 * AbTabControl is a React controlled component that implements an ARIA compatible tabbed navigation UI.
 * It was implemented to cache loaded tabs but to not render tabs that have not been made visible to improve
 * performance and maintain the state of the components in the tabs.
 *
 * Features:
 * - ARIA compatible W3C standard for Automatic Activation
 *   https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-1/tabs.html
 * - multiple themes
 * - lazy loads content when tab is activated and retains content when tab is left to maintain state
 * - 100% React, MobX independent
 *
 * Features to add:
 * - vertical display mode
 * - tab wrapping
 *
 */
export let AbTabControl = observer(_class = (_class2 = (_AbTabControl = class AbTabControl extends React.Component {
  constructor(tabControlProps) {
    super(tabControlProps);
    _defineProperty(this, "activeTabElement", null);
    _defineProperty(this, "justClosedTab", false);
    _defineProperty(this, "tabContainerRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "draggedItem", void 0);
    _initializerDefineProperty(this, "dragLocation", _descriptor, this);
    _defineProperty(this, "hasFocus", () => {
      return !!this.tabContainerRef.current?.contains(document.activeElement) && !!document.activeElement?.classList.contains("ab-tab-button") || this.justClosedTab;
    });
    _defineProperty(this, "makeTabPanelId", tab => {
      return `${tab.id}-panel`;
    });
    _defineProperty(this, "isDraggable", () => {
      return !!this.props.draggable && !!this.props.onTabsChanged;
    });
    _defineProperty(this, "renderTab", (tab, index, lr) => {
      const selected = tab.id === this.props.activeTab;
      const closeFunc = () => {
        if (!this.props.onTabsChanged && !this.props.onCloseTab) {
          LOGGER.error("Cannot close tab without provided onTabsChanged.");
          return;
        }
        const closeImpl = () => {
          if (this.props.onCloseTab) {
            this.props.onCloseTab(tab);
          } else if (this.props.onTabsChanged) {
            const filteredTabs = this.props.tabs.filter(t => t.id !== tab.id);
            const rightFilteredTabs = this.props.rightTabs?.filter(t => t.id !== tab.id) ?? [];
            this.props.onTabsChanged(filteredTabs, rightFilteredTabs);
            this.justClosedTab = true;
            if (tab.id !== this.props.activeTab) return;
            const newActiveTab = this.calcNextTab(filteredTabs.concat(rightFilteredTabs), this.allTabs.findIndex(t => t.id === tab.id));
            if (!newActiveTab) return;
            this.props.onChange(newActiveTab);
          }
        };
        if (tab.queryClose) {
          tab.queryClose().then(value => {
            if (value) {
              closeImpl();
            }
          });
        } else {
          closeImpl();
        }
      };
      const mySideTabs = lr === "left" ? this.props.tabs : this.props.rightTabs ?? [];
      // Don't allow emptying a tab group
      const draggable = mySideTabs.length > 1 && this.isDraggable();
      const draggableEventHandlers = {
        onDragOver: this.isDraggable() ? e => this.onDragOver(e, {
          lr,
          index
        }) : undefined,
        onDragStart: draggable ? e => this.onDragStart(e, tab, {
          lr,
          index
        }) : undefined,
        onDragExit: draggable ? () => this.setDragLocation(undefined) : undefined
      };
      return /*#__PURE__*/React.createElement(AbTabButton, {
        key: tab.id + (tab.closable ? "-with-x" : "-without-x") // This allows button padding css to reset
        ,
        tab: tab,
        isSelected: selected,
        disabled: this.props.disabled || tab.disabled,
        onClose: closeFunc,
        onClick: () => this.props.onChange(tab),
        draggable: draggable,
        abRef: b => {
          if (selected) this.activeTabElement = b;
        },
        ariaControls: this.makeTabPanelId(tab),
        eventHandlers: {
          ...draggableEventHandlers,
          onContextMenu: e => this.props.onTabContextMenu?.(tab, e)
        }
      });
    });
    _defineProperty(this, "calcNextTab", (newTabs, oldIndex) => {
      if (oldIndex === undefined) return newTabs.at(0);
      if (oldIndex < newTabs.length) return newTabs[oldIndex];
      return newTabs.at(-1);
    });
    _defineProperty(this, "setDragLocation", action(location => this.dragLocation = location));
    _defineProperty(this, "onDragOver", (e, location) => {
      e.preventDefault();
      const target = e.currentTarget;
      const targetRectangle = target.getBoundingClientRect();
      this.setDragLocation(AbTabControl.calculateDragLocation(this.draggedItem, location, e.clientX, {
        width: target.offsetWidth,
        left: targetRectangle.left,
        offsetLeft: target.offsetLeft
      }));
    });
    _defineProperty(this, "onDragStart", (e, tab, location) => {
      e.dataTransfer.effectAllowed = "move";
      // Need to get dragged item here
      this.draggedItem = {
        tab,
        location
      };
    });
    _defineProperty(this, "onDrop", () => {
      if (!this.draggedItem) return;
      if (!this.dragLocation) return;
      if (!this.props.onTabsChanged) return;
      this.props.onTabsChanged(AbTabControl.calculateDragResult(this.props.tabs, "left", this.draggedItem.location, this.dragLocation, this.draggedItem.tab), AbTabControl.calculateDragResult(this.props.rightTabs ?? [], "right", this.draggedItem.location, this.dragLocation, this.draggedItem.tab));
      this.draggedItem = undefined;
      this.setDragLocation(undefined);
    });
    _defineProperty(this, "onDragEnd", () => {
      // Cancel drag
      this.draggedItem = undefined;
      this.setDragLocation(undefined);
    });
    makeObservable(this);
  }
  render() {
    /*
     * NOTE: The div with class ab-tab-control-components seems to be unnecessary but it's there to ensure that as
     * the tab control shrinks the tab-contents will not shrink smaller than the tab bar. There needs to be an inline
     * element but not the ab-tab-control because it works better as a block element.
     */

    const contextSize = this.context;
    const sizeClass = this.props.size ?? contextSize;
    // TODO Only add width spacer when it is horizontal
    const beforeClass = StringUtils.cssClassStr("ab-tab-bar-before", this.props.beforeDivClassName, this.props.before ? "" : "empty");
    const afterClass = StringUtils.cssClassStr("ab-tab-bar-after", this.props.afterDivClassName);
    const tabClass = StringUtils.cssClassStr("ab-tab-control", this.props.className, this.props.theme ?? AbTabControlTheme.SECONDARY);
    const tabBarClass = StringUtils.cssClassStr("ab-tab-bar", sizeClass, this.isDraggable() && !this.dragLocation && "ab-tab-bar-allow-knurling");
    const actionButtons = this.props.actionButtons ?? [];
    const toolbarButtons = [];
    if (actionButtons.length > 0) {
      toolbarButtons.push(/*#__PURE__*/React.createElement(React.Fragment, {
        key: "ab-tab-control-toolbar-buttons"
      }, actionButtons.map((buttonProps, index) => /*#__PURE__*/React.createElement(AbButton, _extends({
        key: index
      }, buttonProps)))));
    }
    const hasRightTabs = this.props.rightTabs && this.props.rightTabs.length > 0;
    const inner = /*#__PURE__*/React.createElement("div", {
      className: CssUtils.composeClassNames("ab-tab-bar-wrapper", sizeClass)
    }, /*#__PURE__*/React.createElement("div", {
      className: tabBarClass,
      role: "tablist",
      "aria-disabled": this.props.disabled,
      ref: this.tabContainerRef,
      onDrop: this.onDrop,
      onDragEnd: this.onDragEnd,
      onDragOver: e => e.preventDefault(),
      onKeyDown: e => {
        KeyboardUtilsReact.arrowGroupKeyDown(e, Orientation.HORIZONTAL, focusedElement => {
          const newTab = this.allTabs.find(tab => tab.id === focusedElement.id);
          if (newTab === undefined || newTab.id === this.props.activeTab) return;
          this.props.onChange(newTab);
        });
      }
    }, this.props.before === null ? null : /*#__PURE__*/React.createElement("div", {
      className: beforeClass,
      style: this.props.beforeDivStyle
    }, this.props.before), this.props.tabs.map((tab, index) => this.renderTab(tab, index, "left")), /*#__PURE__*/React.createElement("div", {
      className: afterClass,
      style: this.props.afterDivStyle
    }, this.props.after), toolbarButtons.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "ab-tab-control-button-bar"
    }, toolbarButtons), this.props.rightTabs?.map((tab, index) => this.renderTab(tab, index, "right")), hasRightTabs && /*#__PURE__*/React.createElement("div", {
      className: "ab-tab-bar-after-right"
    }), this.dragLocation && /*#__PURE__*/React.createElement("div", {
      style: {
        left: this.dragLocation.left
      },
      className: "ab-tab-control-drag-separator",
      key: "tab-drag-separator"
    })));
    return /*#__PURE__*/React.createElement("div", {
      "data-test-component": "ab-tab-control",
      "data-test-id": this.props.dataTestId ?? this.props["data-test-id"],
      id: this.props.id,
      className: tabClass,
      style: this.props.style
    }, /*#__PURE__*/React.createElement("div", {
      className: "ab-tab-control-components"
    }, sizeProviderWrapper(inner, this.props, contextSize), this.props.disableCachedTabs ? this.allTabs.map(t => t.id === this.props.activeTab ? this.renderContent(t) : null) : this.allTabs.map(t => this.renderContent(t))));
  }
  componentDidUpdate() {
    // If we were updated and have the focus we need to make sure the focus is correct
    if (this.activeTabElement && this.hasFocus()) this.activeTabElement.focus();
    this.justClosedTab = false;
  }
  renderContent(tab) {
    const selected = tab.id === this.props.activeTab;
    const className = StringUtils.cssClassStr(tab.className, "ab-tab-content", ["tab", tab.id], selected ? "active" : "hidden");
    return /*#__PURE__*/React.createElement("div", {
      id: this.makeTabPanelId(tab),
      key: this.makeTabPanelId(tab),
      style: this.props.contentStyle,
      role: "tabpanel",
      className: className,
      "aria-labelledby": tab.id
    }, renderContentWithIdentityForClassComponent(this, tab.renderWithoutCache ? tab.content : /*#__PURE__*/React.createElement(CachedTabContent, {
      id: tab.id,
      selected: selected,
      content: tab.content
    })));
  }
  get allTabs() {
    return this.props.tabs.concat(this.props.rightTabs ?? []);
  }
}, _defineProperty(_AbTabControl, "contextType", AbSizeContext), _defineProperty(_AbTabControl, "calculateDragLocation", (draggedItem, location, clientX, domProps) => {
  // Need to set index and which half in here
  if (!draggedItem || locationsEqual(location, draggedItem.location)) {
    // If dragging item over itself, it's not a drop location
    return undefined;
  }
  const mouseRelativeXPosition = clientX - domProps.left;
  let dropIndex;
  let left;
  if (mouseRelativeXPosition > domProps.width / 2) {
    // right half drop zone - render line on the left of the next element (unless last element then render right)
    dropIndex = location.index + 1;
    left = domProps.offsetLeft + domProps.width;
  } else {
    // left half drop zone
    dropIndex = location.index;
    left = domProps.offsetLeft;
  }
  const newLocation = {
    index: dropIndex,
    lr: location.lr,
    left
  };
  if (locationsEqual(draggedItem.location, newLocation) || locationsEqual(draggedItem.location, {
    index: dropIndex - 1,
    lr: newLocation.lr
  })) {
    // If dragging item next to the original location, it's not a drop location
    return undefined;
  }
  return newLocation;
}), _defineProperty(_AbTabControl, "calculateDragResult", (tabs, lr, from, to, tab) => {
  const tabsCopy = tabs.slice();
  if (lr !== to.lr) {
    if (lr !== from.lr) {
      return tabsCopy;
    }
    tabsCopy.splice(from.index, 1);
    return tabsCopy;
  }
  tabsCopy.splice(to.index, 0, tab);
  if (lr !== from.lr) {
    return tabsCopy;
  }
  tabsCopy.splice(from.index < to.index ? from.index : from.index + 1, 1);
  return tabsCopy;
}), _AbTabControl), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "dragLocation", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return undefined;
  }
}), _class2)) || _class;

/**
 * CachedTabContentProps are for rendering the private CachedTabContent component for tab internals.
 */

/**
 * CachedTabContent is an internal component that short circuits when the tab being rendered is not active.
 * The inactive tab does not render its content, so it is initially not rendered to the DOM and then when it is
 * inactive it stays on the DOM to maintain its state.
 */
class CachedTabContent extends React.Component {
  /**
   * Lifecyce method that keeps inactive tabs from updating when the main component is rendered.
   * @param {CachedTabContentProps} nextProps
   * @returns {boolean}
   */
  shouldComponentUpdate(nextProps) {
    return nextProps.selected;
  }
  render() {
    return this.props.selected ? this.props.content : null;
  }
}
const AbTabButton = props => {
  const {
    tab,
    isSelected,
    disabled,
    onClick,
    onClose,
    abRef,
    ariaControls
  } = {
    ...props
  };
  const [labelText, setLabelText] = React.useState("");
  const labelRef = AbHooks.useMutationObserver(span => {
    setLabelText(span.innerText);
  });
  return /*#__PURE__*/React.createElement(AbButton, {
    tooltip: tab.tooltip === true ? tab.label : tab.tooltip,
    tooltipProps: {
      clippedTrigger: labelRef,
      clippedTooltip: /*#__PURE__*/React.createElement(AbItem, {
        icon: tab.icon,
        label: /*#__PURE__*/React.createElement("span", {
          className: "ab-tab-button-label"
        }, tab.label)
      })
    },
    id: tab.id,
    key: tab.id,
    dataTestId: `${tab.id}-tab`,
    className: StringUtils.cssClassStr(tab.className, "ab-tab-button", ["tab", tab.id], isSelected && "active", tab.closable && "ab-tab-button-closable"),
    tabIndex: isSelected ? undefined : -1,
    onClick: onClick,
    abRef: abRef,
    disabled: disabled,
    __squishyPadding: true,
    label: /*#__PURE__*/React.createElement("div", {
      className: "ab-tab-button-contents-wrapper"
    }, /*#__PURE__*/React.createElement(AbItem, {
      icon: tab.icon,
      label: /*#__PURE__*/React.createElement("span", {
        className: "ab-tab-button-label",
        ref: labelRef
      }, tab.label)
    }), tab.closable && /*#__PURE__*/React.createElement(AbPseudoButton, {
      className: "ab-tab-close-button",
      icon: "absdk:close",
      size: AbSize.SMALL,
      ariaLabel: AbReactResources.tabControl_closeTab_button_ariaLabel(labelText),
      tooltip: AbReactResources.tabControl_closeTab_button_ariaLabel(labelText),
      onClick: onClose
    })),
    eventHandlers: {
      ...props.eventHandlers,
      onKeyDown: e => {
        if (e.key === KeyValue.DELETE && tab.closable) {
          onClose();
        }
        props.eventHandlers?.onKeyDown?.(e);
      },
      onAuxClick: e => {
        props.eventHandlers?.onAuxClick?.(e);
        // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button#value
        // button 0 -> left click. Not called by onAuxClick
        // button 1 -> middle click
        // button 2 -> right click
        if (e.button !== 1) return;
        e.preventDefault();
        e.stopPropagation();
        tab.closable && onClose();
      }
    },
    __htmlProps: {
      role: "tab",
      "aria-selected": isSelected,
      "aria-controls": ariaControls,
      draggable: props.draggable
    }
  });
};
//# sourceMappingURL=AbTabControl.js.map
